/* USE .bd-slider class to initialize slider, do not add the .bd-slider class to thumbs using data-slider-thumbs=".class" will initialize the slider for the thumbnails - each slider needs to be wrapped in 
<bd-slider>
<div>
<div class="bd-track">
<div (or any element) class=".slide">
*/

class bdSlider extends HTMLElement {
  constructor() {
    super();
    this._resizeHandler = this.handleResize.bind(this);
  }

  connectedCallback() {
    // Attach resize listener  once
    window.addEventListener('resize', this._resizeHandler);
    // Initialize slider
    this.initSlider();
  }
  initSlider() {
    this.slider = this.querySelector('.bd-slider');
    if (!this.slider) return;

    const dataset = this.slider.dataset;
    const disableSetting = dataset.sliderDisable;
    // Check breakpoint disable BEFORE initializing Embla
    if (disableSetting && this.isDisabledForViewport(disableSetting)) {
      this.destroySlider();
      return; 
    }

    // Prevent double initialization
    if (this.embla) return;
    const autoplayMs = dataset.autoplay ? parseInt(dataset.autoplay) : 0;
    const scrollSpeed = dataset.scrollSpeed
      ? parseInt(dataset.scrollSpeed)
      : null;
    const fadeMode = dataset.fade === 'true';

    // Base Embla options
    const options = {
      loop: dataset.wrapAround === 'true',
      align: dataset.align || 'start',
      draggable: !fadeMode,
      containScroll: fadeMode ? false : 'trimSnaps',
      speed: fadeMode ? 0 : 10,
      containerSelector: '.bd-track',
      slides: '.bd-track > .slide',
    };

    // Decide plugins based on scrollSpeed
    const plugins = [];
    if (scrollSpeed) {
      plugins.push(
        EmblaCarouselAutoScroll({
          speed: scrollSpeed,
          stopOnInteraction: false,
          target: 'snap',
        })
      );
    }

    if (fadeMode) {
      plugins.push(EmblaCarouselFade());
    } else if (scrollSpeed) {
      plugins.push(
        EmblaCarouselAutoScroll({
          speed: scrollSpeed,
          stopOnInteraction: false,
          target: 'snap',
        })
      );
    }

    // Initialize Embla
    this.embla = EmblaCarousel(this.slider, options, plugins);
    console.log('Snap points:', this.embla.scrollSnapList());

    // Initial Slide Start
    const initialSlide = parseInt(this.slider.dataset.initialSlide);
    if (!isNaN(initialSlide)) {
      this.embla.scrollTo(initialSlide, true); // jump = true → no animation
    }

    this.setupThumbnails();

    // AutoScroll interaction management
    if (scrollSpeed) {
      const autoScroll = this.embla.plugins().autoScroll;
      const viewport = this.embla
        .rootNode()
        .querySelector('.bd-track').parentElement;

      const stopAuto = () => autoScroll.stop();
      viewport.addEventListener('pointerdown', stopAuto);
      viewport.addEventListener('mouseenter', stopAuto);
      viewport.addEventListener('mouseleave', () => {
        autoScroll.play({ jump: true });
      });
    }
    // Mode: Step‑by‑step autoplay
    if (!scrollSpeed && autoplayMs > 0) {
      this.startAutoplay(autoplayMs);
    }

    // Hide controls if only one slide
    if (this.embla.slideNodes().length <= 1) {
      this.querySelector('[data-prev]')?.classList.add('visually-hidden');
      this.querySelector('[data-next]')?.classList.add('visually-hidden');
      this.querySelector('.slider-dots')?.classList.add('visually-hidden');
    }

    this.setupDots();
    this.setupArrows();
    this.setupPrevNext();
    this.setupCustomPagination(); 

    console.log('Embla slides:', this.embla.slideNodes());
  }

  // END CALLBACK

  // --- FADE‑MODE SAFE THUMBNAIL SYNC (NO JUMP, NO DRIFT, NO MISSING SLIDES) ---
setupThumbnails() {
  const thumbsSelector = this.slider.dataset.sliderThumbs;
  if (!thumbsSelector) return;

  const thumbsRoot = document.querySelector(thumbsSelector);
  if (!thumbsRoot) return;

  // Thumbnails MUST snap for stable scrollTo
  const thumbsEmbla = EmblaCarousel(thumbsRoot, {
    align: 'start',
    containScroll: 'keepSnaps',
    dragFree: false,
    slides: '.bd-track > .slide',
  });

  const thumbSlides = thumbsEmbla.slideNodes();

  // --- INITIAL SYNC ---
  const initialSlide = parseInt(this.slider.dataset.initialSlide);
  if (!isNaN(initialSlide)) {
    queueMicrotask(() => thumbsEmbla.scrollTo(initialSlide, true));
  }

  // --- CLICK → MAIN SLIDER ---
  thumbSlides.forEach((thumb, index) => {
    thumb.addEventListener('click', () => {
      this.embla.scrollTo(index, true); // fade mode → instant index change
    });
  });

  // --- MAIN SLIDER → THUMBS ---
  const updateThumbs = () => {
    const selected = this.embla.selectedScrollSnap();

    // Highlight active thumb
    thumbSlides.forEach((thumb, index) => {
      thumb.classList.toggle('is-active', index === selected);
    });

    // Scroll thumbnails WITHOUT relying on main slider scroll model
    thumbsEmbla.scrollTo(selected, false);
  };
 
  this.embla.on('select', updateThumbs);
  updateThumbs();
}



  // Step‑by‑step autoplay logic
  startAutoplay(autoplayMs) {
    const run = () => this.embla.scrollNext();
    this.autoplayId = setInterval(run, autoplayMs);

    // Pause on hover/press
    this.slider.addEventListener('mouseenter', () =>
      clearInterval(this.autoplayId)
    );
    this.slider.addEventListener('pointerdown', () =>
      clearInterval(this.autoplayId)
    );

    // Resume on leave/release
    const resume = () => {
      clearInterval(this.autoplayId);
      this.autoplayId = setInterval(run, autoplayMs);
    };
    this.slider.addEventListener('mouseleave', resume);
    this.slider.addEventListener('pointerup', resume);
  }

  scrollTo(index) {
    this.embla?.scrollTo(index);
  }

  setupDots() {
    const showDots = this.slider.dataset.dots === 'true';
    if (!showDots) return;

    // Always append dots outside the track, at the end of the bd-slider viewport
    let dotsContainer = this.querySelector('.slider-dots');
    if (!dotsContainer) {
      dotsContainer = document.createElement('div');
      dotsContainer.className = 'slider-dots';
      this.slider.after(dotsContainer); // place after the .bd-slider element
    }

    const slides = this.embla.slideNodes();
    dotsContainer.innerHTML = '';

    slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'slide-dot';
      dot.type = 'button';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.addEventListener('click', () => this.embla.scrollTo(index));
      dotsContainer.appendChild(dot);
    });

    const updateDots = () => {
      const selected = this.embla.selectedScrollSnap();
      [...dotsContainer.children].forEach((dot, i) => {
        dot.classList.toggle('is-selected', i === selected);
      });
    };

    this.embla.on('select', updateDots);
    updateDots();
  }

  setupArrows() {
    const showArrows = this.slider.dataset.arrows === 'true';
    if (!showArrows) return;

    // Check if arrows already exist
    let prevBtn = this.querySelector('[data-prev]');
    let nextBtn = this.querySelector('[data-next]');

    if (!prevBtn) {
      prevBtn = document.createElement('button');
      prevBtn.className = 'prev';
      prevBtn.setAttribute('data-prev', '');
      prevBtn.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M12 15l-5-5 5-5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
      this.insertBefore(prevBtn, this.slider); // place before viewport
    }

    if (!nextBtn) {
      nextBtn = document.createElement('button');
      nextBtn.className = 'next';
      nextBtn.setAttribute('data-next', '');
      nextBtn.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <path d="M8 5l5 5-5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
      this.slider.appendChild(nextBtn); // place after track inside viewport
    }
  }

  setupPrevNext() {
    const prevBtn = this.querySelector('[data-prev]');
    const nextBtn = this.querySelector('[data-next]');

    prevBtn?.addEventListener('click', () => this.embla.scrollPrev());
    nextBtn?.addEventListener('click', () => this.embla.scrollNext());

    // Optional: disable buttons when you can’t scroll further (non-loop mode)
    const updateButtons = () => {
      if (prevBtn) prevBtn.disabled = !this.embla.canScrollPrev();
      if (nextBtn) nextBtn.disabled = !this.embla.canScrollNext();
    };

    this.embla.on('select', updateButtons);
    updateButtons();
  }

  // Custom pagination logic
  setupCustomPagination() {
    if (!this.slider.hasAttribute('data-custom-pag')) return;

    const customPrev = this.querySelector('.prev-arrow');
    const customNext = this.querySelector('.next-arrow');
    const customPrevText = this.querySelector('[data-prev-text]');
    const customNextText = this.querySelector('[data-next-text]');

    const updatePagNames = () => {
      const slides = this.embla.slideNodes();
      const currentIndex = this.embla.selectedScrollSnap();
      const nextIndex = (currentIndex + 1) % slides.length;
      const prevIndex = (currentIndex - 1 + slides.length) % slides.length;

      customNextText.textContent = slides[nextIndex]?.dataset.title || '';
      customPrevText.textContent = slides[prevIndex]?.dataset.title || '';
    };

    this.embla.on('select', () => {
      const slides = this.embla.slideNodes();
      updatePagNames();
      slides.forEach((slide, index) => {
        slide.setAttribute(
          'aria-hidden',
          index === this.embla.selectedScrollSnap() ? 'false' : 'true'
        );
        slide.setAttribute(
          'tabindex',
          index === this.embla.selectedScrollSnap() ? '0' : '-1'
        );
      });
    });

    customPrev?.addEventListener('click', () => this.embla.scrollPrev());
    customNext?.addEventListener('click', () => this.embla.scrollNext());

    updatePagNames();
  }

  isDisabledForViewport(disableSetting) {
    const width = window.innerWidth;

    switch (disableSetting) {
      case 'mobile':
        return width < 749;
      case 'tablet':
        return width >= 749 && width < 1024;
      case 'desktop':
        return width >= 1024;
      case 'tablet-up':
        return width >= 768;
      case 'mobile-only':
        return width < 480;
      default:
        return false;
    }
  }

  handleResize() {
    const disableSetting = this.slider?.dataset.sliderDisable;
    if (!disableSetting) return;
    const shouldDisable = this.isDisabledForViewport(disableSetting);
    if (shouldDisable) {
      this.destroySlider();
    } else {
      this.initSlider();
    }
  }

  destroySlider() {
    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }
  }
}

customElements.define('bd-slider', bdSlider);
